<template>
  <article class="rounded-2xl border border-foxy-border bg-foxy-surface/80 p-4 text-foxy-text shadow-sm sm:p-5"
    :class="!isEditing ? 'cursor-pointer hover:border-foxy-action/40 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20' : ''"
    :role="!isEditing && factUrl ? 'link' : undefined" :tabindex="!isEditing && factUrl ? 0 : undefined"
    :aria-label="!isEditing ? 'Відкрити факт з книги «' + fact.book?.title + '»' : undefined"
    data-account-fact-card @click="navigateFact" @keydown="handleCardKeydown">
    <div class="flex items-start gap-3">
      <a v-if="bookUrl" :href="bookUrl" class="shrink-0 self-start rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action/30">
        <img :src="resolveBookCover(fact.book && fact.book.cover)" :alt="fact.book ? fact.book.title : 'Книга'"
          class="h-14 w-10 rounded-lg border border-foxy-border object-cover" loading="lazy" decoding="async" @error="onCoverError">
      </a>

      <div class="min-w-0 flex-1">
        <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div class="min-w-0">
            <a v-if="bookUrl" :href="bookUrl" class="break-words text-sm font-bold text-foxy-text hover:text-foxy-action">
              {{ fact.book ? fact.book.title : 'Книга' }}
            </a>
            <div v-else class="break-words text-sm font-bold text-foxy-text">{{ fact.book ? fact.book.title : 'Книга' }}</div>
            <time :datetime="fact.created_at" :title="formatDate(fact.created_at)" class="mt-1 block text-xs text-foxy-text-secondary">{{ fact.created_at_human || formatDate(fact.created_at) }}</time>
          </div>

          <div v-if="fact.can_manage && !isEditing" class="flex flex-wrap gap-2">
            <button type="button" class="acc-btn !px-3 !py-1.5 text-xs" @click="startEdit">Редагувати</button>
            <button type="button" class="acc-btn !px-3 !py-1.5 text-xs !text-foxy-error" :disabled="isDeleting" @click="deleteFact">
              {{ isDeleting ? 'Видалення…' : 'Видалити' }}
            </button>
          </div>
        </div>

        <div v-if="isEditing" class="mt-4">
          <label :for="`fact-content-${fact.id}`" class="sr-only">Текст факту</label>
          <textarea :id="`fact-content-${fact.id}`" v-model="editContent" class="foxy-textarea !min-h-[10rem] !w-full !resize-y !p-3"
            maxlength="1000" placeholder="Введіть цікавий факт..." />
          <div class="mt-1 flex items-center justify-between gap-3 text-xs text-foxy-text-secondary">
            <span>Від 50 до 1000 символів</span>
            <span>{{ editContent.length }} / 1000</span>
          </div>
          <label :for="`account-fact-source-url-${fact.id}`" class="mb-1 mt-4 block text-sm font-semibold text-foxy-text">
            Джерело <span class="font-normal text-foxy-text-secondary">(необов’язково)</span>
          </label>
          <input :id="`account-fact-source-url-${fact.id}`" v-model="editSourceUrl" type="url" maxlength="1024"
            class="foxy-input w-full" placeholder="https://example.com/material">
          <p v-if="editError" class="mt-2 text-sm text-foxy-error" role="alert">{{ editError }}</p>
          <div class="mt-3 flex flex-wrap gap-2">
            <button type="button" class="acc-btn-primary" :disabled="isSaving" @click="saveEdit">
              {{ isSaving ? 'Збереження…' : 'Зберегти' }}
            </button>
            <button type="button" class="acc-btn" :disabled="isSaving" @click="cancelEdit">Скасувати</button>
          </div>
        </div>

        <div v-else class="mt-4">
          <p ref="content" class="whitespace-pre-line break-words text-sm leading-relaxed text-foxy-text"
            :class="{ 'line-clamp-4': !isExpanded }">{{ fact.content }}</p>
          <a v-if="fact.source_url" :href="fact.source_url" target="_blank" rel="noopener noreferrer"
            class="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-foxy-action hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action/30">
            Джерело
            <i class="fas fa-external-link-alt text-xs" aria-hidden="true"></i>
          </a>
          <button v-if="isExpanded || isOverflowing" type="button"
            class="mt-2 text-sm font-semibold text-foxy-action hover:underline" @click="toggleExpanded">
            {{ isExpanded ? 'Згорнути' : 'Розгорнути' }}
          </button>
        </div>

        <footer class="mt-4 flex flex-wrap items-center gap-4 text-sm text-foxy-text-secondary">
          <button v-if="fact.can_like" type="button" class="inline-flex min-h-11 items-center gap-1.5 rounded hover:text-foxy-text disabled:cursor-wait"
            :aria-label="isLiked ? 'Прибрати вподобання' : 'Вподобати факт'" :aria-pressed="isLiked ? 'true' : 'false'"
            :disabled="isLikePending" :aria-busy="isLikePending ? 'true' : 'false'" @click="toggleLike">
            <i class="fas fa-heart" :class="isLiked ? 'text-red-500 dark:text-red-400' : 'text-foxy-text-secondary'" aria-hidden="true"></i>{{ likesCount }}
          </button>
          <span v-else class="inline-flex min-h-11 items-center gap-1.5" aria-label="Кількість вподобань">
            <i class="fas fa-heart text-foxy-text-secondary" aria-hidden="true"></i>{{ likesCount }}
          </span>
          <report-button v-if="fact.can_report" class="ml-auto" :comfortable-trigger="true" :reportable-type="'App\\Models\\Fact'" :reportable-id="fact.id"
            :content-preview="String(fact.content || '').slice(0, 100)" :content-url="factUrl" />
        </footer>
      </div>
    </div>
  </article>
</template>

<script lang="js">
import axios from 'axios';

export default {
  name: 'AccountFactCard',
  props: { fact: { type: Object, required: true }, publicationContext: { type: Object, default: null } },
  data() {
    return {
      isExpanded: false,
      isOverflowing: false,
      isEditing: false,
      editContent: '',
      editSourceUrl: '',
      editError: '',
      isSaving: false,
      isDeleting: false,
      isLiked: Boolean(this.fact.is_liked_by_current_user),
      likesCount: Number(this.fact.likes_count || 0),
      isLikePending: false,
    };
  },
  computed: {
    bookUrl() {
      return this.fact?.book?.slug ? `/books/${this.fact.book.slug}` : '';
    },
    factUrl() {
      return this.bookUrl ? `${this.bookUrl}/facts?fact=${this.fact.id}#fact-${this.fact.id}` : '';
    },
  },
  watch: {
    'fact.likes_count'(value) { this.likesCount = Number(value || 0); },
    'fact.is_liked_by_current_user'(value) { this.isLiked = Boolean(value); },
  },
  mounted() {
    this.$nextTick(this.checkOverflow);
    window.addEventListener('resize', this.checkOverflow);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkOverflow);
  },
  methods: {
    navigateFact(event) {
      if (this.isEditing || !this.factUrl || event?.defaultPrevented) return;
      if (event?.target?.closest?.('a, button, input, select, textarea, label, [role="menu"], [role="dialog"], [contenteditable="true"]')) return;
      if (window.getSelection?.()?.toString()) return;
      window.location.assign(this.factUrl);
    },
    handleCardKeydown(event) {
      if (event.target !== event.currentTarget || !['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      if (!event.repeat) this.navigateFact({ target: event.target });
    },
    async toggleLike() {
      if (!this.fact.can_like || this.isLikePending) return;
      this.isLikePending = true;
      try {
        const { data } = await axios.post(this.bookUrl + '/facts/' + this.fact.id + '/like');
        this.isLiked = Boolean(data.is_liked);
        this.likesCount = Number(data.likes_count || 0);
      } catch (error) {
        this.$emit('notice', error.response?.data?.message || 'Не вдалося змінити вподобання.', 'error');
      } finally { this.isLikePending = false; }
    },

    resolveBookCover(cover) {
      return cover || '/images/placeholders/book-cover.svg';
    },
    onCoverError(event) {
      const fallback = '/images/placeholders/book-cover.svg';
      if (event?.target && !String(event.target.src || '').includes(fallback)) event.target.src = fallback;
    },
    formatDate(value) {
      if (!value) return '';
      return new Date(value).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    checkOverflow() {
      if (this.isExpanded) return;
      this.$nextTick(() => {
        const element = this.$refs.content;
        this.isOverflowing = Boolean(element && element.scrollHeight > element.clientHeight + 1);
      });
    },
    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
      if (!this.isExpanded) this.$nextTick(this.checkOverflow);
    },
    startEdit() {
      this.editContent = String(this.fact.content || '');
      this.editSourceUrl = String(this.fact.source_url || '');
      this.editError = '';
      this.isEditing = true;
    },
    cancelEdit() {
      this.isEditing = false;
      this.editContent = '';
      this.editSourceUrl = '';
      this.editError = '';
    },
    async saveEdit() {
      if (this.isSaving || !this.fact.can_manage) return;
      const content = this.editContent.trim();
      if (content.length < 50 || content.length > 1000) {
        this.editError = 'Факт повинен містити від 50 до 1000 символів.';
        return;
      }

      this.isSaving = true;
      this.editError = '';
      try {
        const response = await axios.put(`/books/${this.fact.book.slug}/facts/${this.fact.id}`, {
          content,
          source_url: this.editSourceUrl.trim() || null,
        });
        this.$emit('updated', { ...this.fact, ...response.data.fact, content });
        this.$emit('notice', 'Факт оновлено.', 'success');
        this.isEditing = false;
        this.$nextTick(this.checkOverflow);
      } catch (error) {
        this.editError = error.response?.data?.errors?.source_url?.[0]
          || error.response?.data?.errors?.content?.[0]
          || error.response?.data?.message
          || 'Не вдалося оновити факт.';
      } finally {
        this.isSaving = false;
      }
    },
    async deleteFact() {
      if (this.isDeleting || !this.fact.can_manage) return;
      const context = { ...this.publicationContext, family: 'fact', id: this.fact.id };
      const url = `/books/${this.fact.book.slug}/facts/${this.fact.id}`;
      this.isDeleting = true;
      let started = false;
      try {
        const confirmed = await confirm('Видалити цю публікацію? Дію неможливо скасувати.', 'Підтвердження', 'warning');
        if (!confirmed || this._isDestroyed) return;
        started = true;
        this.$emit('publication-mutation-start', context);
        const { data } = await axios.delete(url);
        if (data?.success !== true) throw new Error(data?.message || 'Не вдалося видалити публікацію.');
        this.$emit('deleted', context.id, data.facts_count, context);
        this.$emit('notice', 'Публікацію видалено.', 'success');
      } catch (error) {
        this.$emit('notice', error.response?.data?.message || error.message || 'Не вдалося видалити публікацію.', 'error');
      } finally {
        this.isDeleting = false;
        if (started) {
          if (context.finish) context.finish(context);
          else this.$emit('publication-mutation-finish', context);
        }
      }
    },

  },
};
</script>
